import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api.js';
import { FileText, Download, Calendar, Filter, ChevronDown, ChevronUp } from 'lucide-react';

interface DistributorReport {
  id: string;
  name: string;
  contact_email?: string;
  orders: Array<{
    customer_name: string;
    customer_number: string;
    product_name: string;
    product_brand: string;
    quantity: number;
    promotion_type: string;
    order_date: string;
  }>;
}

const DistributorOrderReport: React.FC = () => {
  const [report, setReport] = useState<{ distributors: DistributorReport[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedDistributors, setExpandedDistributors] = useState<Set<string>>(new Set());
  
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
  });

  const fetchReport = async () => {
    if (!filters.date_from || !filters.date_to) {
      toast.warning('Please select a time period');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('date_from', filters.date_from);
      params.append('date_to', filters.date_to);

      const res = await api.get(`/orders/report/distributors?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch report', err);
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const toggleDistributor = (id: string) => {
    const newExpanded = new Set(expandedDistributors);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedDistributors(newExpanded);
  };

  const exportToCSV = (dist: DistributorReport) => {
    const header = ['Store', 'Store Number', 'Product', 'Brand', 'Quantity', 'Promotion', 'Date'];
    const rows = dist.orders.map(order => [
      order.customer_name,
      order.customer_number || '–',
      order.product_name,
      order.product_brand,
      order.quantity,
      order.promotion_type === 'none' ? 'None' : order.promotion_type,
      new Date(order.order_date).toLocaleDateString('en-US'),
    ]);

    const csv = [header, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${dist.name}_Orders_${filters.date_from}_${filters.date_to}.csv`;
    link.click();
  };

  const copyToClipboard = (dist: DistributorReport) => {
    const text = `
Orders for ${dist.name}
Period: ${new Date(filters.date_from).toLocaleDateString('en-US')} - ${new Date(filters.date_to).toLocaleDateString('en-US')}
Total: ${dist.orders.length} orders

${dist.orders.map(order => 
  `${order.customer_name} (${order.customer_number || 'N/A'}) - ${order.product_name} (${order.product_brand}) - ${order.quantity} pcs - ${order.promotion_type === 'none' ? 'No action' : order.promotion_type}`
).join('\n')}
    `.trim();

    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getTotalOrders = () => {
    if (!report) return 0;
    return report.distributors.reduce((sum, dist) => sum + dist.orders.length, 0);
  };

  const getPromotionLabel = (promo: string) => {
    if (promo === 'none') return 'Keine';
    return promo;
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 rounded-xl">
            <FileText size={28} className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900">Lieferanten-Bestellreport</h1>
            <p className="text-gray-600 text-sm">Exportieren Sie Bestellungen gruppiert nach Lieferant</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-gray-600" />
          <h2 className="text-lg font-bold text-gray-900">Filter</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Von Datum <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Bis Datum <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <Calendar size={18} />
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {report && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-2xl font-black mb-2">Report Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-center mt-4">
              <div>
                <p className="text-3xl font-black">{report.distributors.length}</p>
                <p className="text-sm opacity-90">Distributors</p>
              </div>
              <div>
                <p className="text-3xl font-black">{getTotalOrders()}</p>
                <p className="text-sm opacity-90">Orders</p>
              </div>
              <div>
                <p className="text-3xl font-black">
                  {new Date(filters.date_from).toLocaleDateString('en-US')} - {new Date(filters.date_to).toLocaleDateString('en-US')}
                </p>
                <p className="text-sm opacity-90">Period</p>
              </div>
            </div>
          </div>

          {/* Distributors */}
          {report.distributors.map(dist => {
            const isExpanded = expandedDistributors.has(dist.id);

            return (
              <div key={dist.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* Distributor Header */}
                <div
                  className="flex items-center justify-between p-6 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleDistributor(dist.id)}
                >
                  <div className="flex-1">
                    <h3 className="text-xl font-black text-gray-900">{dist.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {dist.orders.length} order{dist.orders.length !== 1 ? 's' : ''}
                      {dist.contact_email && ` • ${dist.contact_email}`}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportToCSV(dist);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-bold"
                    >
                      <Download size={16} />
                      CSV
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(dist);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-bold"
                    >
                      <FileText size={16} />
                      Copy
                    </button>
                    {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </div>

                {/* Orders Table (Collapsible) */}
                {isExpanded && (
                  <div className="p-6">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-2 text-xs font-bold text-gray-600 uppercase">Store</th>
                          <th className="text-left py-3 px-2 text-xs font-bold text-gray-600 uppercase">Store #</th>
                          <th className="text-left py-3 px-2 text-xs font-bold text-gray-600 uppercase">Product</th>
                          <th className="text-right py-3 px-2 text-xs font-bold text-gray-600 uppercase">Quantity</th>
                          <th className="text-left py-3 px-2 text-xs font-bold text-gray-600 uppercase">Promotion</th>
                          <th className="text-left py-3 px-2 text-xs font-bold text-gray-600 uppercase">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dist.orders.map((order, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-2 font-semibold text-gray-900">{order.customer_name}</td>
                            <td className="py-3 px-2 text-sm text-gray-600 font-mono">
                              {order.customer_number || <span className="text-gray-400">–</span>}
                            </td>
                            <td className="py-3 px-2 text-sm">
                              <div>
                                <p className="font-semibold text-gray-900">{order.product_name}</p>
                                <p className="text-xs text-gray-500">{order.product_brand}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right font-bold text-gray-900">{order.quantity}</td>
                            <td className="py-3 px-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                order.promotion_type === 'none' 
                                  ? 'bg-gray-100 text-gray-600' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {getPromotionLabel(order.promotion_type)}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString('de-DE')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {report.distributors.length === 0 && (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
              <FileText size={64} className="mx-auto mb-4 text-gray-400" />
              <p className="text-xl font-bold text-gray-600 mb-2">Keine Bestellungen im ausgewählten Zeitraum</p>
              <p className="text-gray-500">Ändern Sie den Filter und versuchen Sie es erneut.</p>
            </div>
          )}
        </div>
      )}

      {!report && !loading && (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
          <Calendar size={64} className="mx-auto mb-4 text-gray-400" />
          <p className="text-xl font-bold text-gray-600 mb-2">Wählen Sie einen Zeitraum</p>
          <p className="text-gray-500">Generieren Sie einen Report, um Bestellungen nach Lieferant zu sehen.</p>
        </div>
      )}
    </div>
  );
};

export default DistributorOrderReport;

